import * as Stimulus from "@hotwired/stimulus"

/**
 * A box that reads the two date inputs inside the dialog it opens. See the
 * range box in App.Handler.Admin.Invites.Drafts.
 *
 * The inputs are the form's real fields and sit inside the form, so nothing is
 * copied across on apply: this only formats what they hold and puts the dialog
 * back the way it found it when the admin cancels out of it.
 */
export class DateRangeBoxController extends Stimulus.Controller {
    static get targets() {
        return [ "chip", "summary", "placeholder" ];
    }

    static get values() {
        return { startName: String, endName: String };
    }

    connect() {
        this.remember();
        this.render();
        this.dialog?.addEventListener("close", () => this.restore());
    }

    get dialog() {
        return this.element.querySelector("dialog");
    }

    get startInput() {
        return this.element.querySelector(`input[name="${this.startNameValue}"]`);
    }

    get endInput() {
        return this.element.querySelector(`input[name="${this.endNameValue}"]`);
    }

    apply() {
        const start = this.startInput;
        const end = this.endInput;
        // One date alone reads as a range on the page but reaches the server as
        // no range at all, so it is refused here rather than silently dropped.
        if (Boolean(start.value) !== Boolean(end.value)) {
            const missing = start.value ? end : start;
            missing.setCustomValidity("Pick both dates, or neither.");
            missing.reportValidity();
            missing.addEventListener("input", () => missing.setCustomValidity(""), { once: true });
            return;
        }

        this.remember();
        this.render();
        this.dialog?.close();
    }

    clear() {
        this.startInput.value = "";
        this.endInput.value = "";
        this.remember();
        this.render();
    }

    remember() {
        this.saved = [ this.startInput.value, this.endInput.value ];
    }

    restore() {
        [ this.startInput.value, this.endInput.value ] = this.saved;
        this.render();
    }

    render() {
        const start = this.startInput.value;
        const end = this.endInput.value;
        const set = Boolean(start && end);
        this.chipTarget.classList.toggle("hidden", !set);
        this.placeholderTarget.classList.toggle("hidden", set);
        if (set) {
            this.summaryTarget.textContent = `${shortDate(start)} - ${shortDate(end)}`;
        }
    }
}

// Split rather than parsed as a Date, which reads a bare yyyy-mm-dd as UTC and
// can land the day before in the browser's own zone.
function shortDate(iso) {
    const [ year, month, day ] = iso.split("-");
    return `${Number(month)}/${Number(day)}/${year.slice(2)}`;
}
